import { isSupportedLocale, resolveEffectiveLocale } from '../../shared/locales.mjs';

// Presentation hints only. Never consumed by authentication, permissions or tenant guards.
const PREFIX = 'privana:portal-locale:v1:';
const MAX_HINTS = 12;
const listeners = new Set();
let active = null;
let generation = 0;
let revision = 0;
let sequence = 0;
let appliedSequence = 0;
let personalSaveSequence = 0;
let clubSaveSequence = 0;
let snapshot = null;
const emit = () => listeners.forEach(listener => listener());
export const subscribePortalLocale = listener => { listeners.add(listener); return () => listeners.delete(listener); };
export const getPortalLocaleSnapshot = () => snapshot;

export function portalLocaleScope({ subject, clubCode, context }) {
  return subject && clubCode && context ? JSON.stringify([context, subject, String(clubCode)]) : null;
}

export function readPortalLocale(scope) {
  if (!scope) return null;
  try {
    const value = JSON.parse(window.localStorage.getItem(PREFIX + scope));
    if (value?.version !== 1 || !(value.preferred_locale === null || isSupportedLocale(value.preferred_locale)) || !isSupportedLocale(value.club_default_locale)) return null;
    return { ...value, personalKnown: true, clubKnown: true, locale: resolveEffectiveLocale({ individualLocale: value.preferred_locale, clubLocale: value.club_default_locale }) };
  } catch { return null; }
}

function persist(scope, preference) {
  try {
    const storage = window.localStorage;
    // Bound the cache without reading any other browser storage values.
    const keys = Object.keys(storage).filter(key => key.startsWith(PREFIX) && key !== PREFIX + scope);
    while (keys.length >= MAX_HINTS) storage.removeItem(keys.shift());
    storage.removeItem(PREFIX + scope);
    storage.setItem(PREFIX + scope, JSON.stringify({ version: 1, preferred_locale: preference.preferred_locale, club_default_locale: preference.club_default_locale }));
  } catch { /* Private mode / quota: memory still works. */ }
}

export function activatePortalLocale(scope, clubCode) {
  if (active?.scope === scope && active?.clubCode === String(clubCode)) return;
  generation += 1;
  revision = appliedSequence = personalSaveSequence = clubSaveSequence = 0;
  active = scope ? { scope, clubCode: String(clubCode) } : null;
  snapshot = scope ? { scope, preference: readPortalLocale(scope), settled: false } : null;
  emit();
}

export function capturePortalLocaleRequest(clubCode) {
  return active && active.clubCode === String(clubCode)
    ? { ...active, generation, revision, sequence: ++sequence, clubSaveSequence }
    : null;
}

function current(ticket) {
  return ticket && ticket.scope === active?.scope && ticket.generation === generation;
}

export function resolvePortalLocaleRequest(ticket, data, { saved = false, clubDefaultOnly = false } = {}) {
  if (!current(ticket) || (!saved && (ticket.revision !== revision || ticket.sequence < appliedSequence))) return;
  if (saved && ticket.sequence < (clubDefaultOnly ? clubSaveSequence : personalSaveSequence)) return;
  const previous = snapshot?.preference;
  const preservePersonal = previous?.personalKnown && (clubDefaultOnly || data?.personal_locale_resolved === false);
  const preferred = preservePersonal ? previous.preferred_locale : data?.preferred_locale;
  // Club and personal saves affect independent slots. An older personal response
  // may carry an outdated club default, which must be ignored.
  const preserveClub = previous?.clubKnown && (
    (saved && !clubDefaultOnly && ticket.clubSaveSequence !== clubSaveSequence)
    || data?.club_locale_resolved === false
  );
  const clubDefault = preserveClub ? previous.club_default_locale
    : data?.club_default_locale ?? data?.default_locale ?? previous?.club_default_locale;
  // Absent is unresolved; null explicitly means inheritance. Failed reads use
  // independent known slots without turning a synthesized null into inheritance.
  const personalKnown = Boolean(preservePersonal || (data?.personal_locale_resolved !== false
    && (preferred === null || isSupportedLocale(preferred))));
  const clubKnown = Boolean(preserveClub || (data?.club_locale_resolved !== false && isSupportedLocale(clubDefault)));
  const preference = {
    preferred_locale: preferred,
    club_default_locale: clubDefault,
    personalKnown,
    clubKnown,
    locale: resolveEffectiveLocale({ individualLocale: preferred, clubLocale: clubDefault }),
  };
  const valid = data?.locale_preferences_resolved !== false && personalKnown && clubKnown;
  if (saved) {
    revision += 1;
    if (clubDefaultOnly) clubSaveSequence = ticket.sequence;
    else personalSaveSequence = ticket.sequence;
  }
  appliedSequence = Math.max(appliedSequence, ticket.sequence);
  snapshot = { scope: ticket.scope, preference, settled: true };
  if (valid) persist(ticket.scope, preference);
  emit();
}

export function failPortalLocaleRequest(ticket) {
  if (!current(ticket) || ticket.revision !== revision || ticket.sequence < appliedSequence) return;
  snapshot = { ...snapshot, settled: true };
  emit();
}
